/**
 * studyPlanCacheService.js
 * Persists study plan state (plan JSON, progress, step content, quiz results)
 * to Firestore — one document per (userId, sourceId).
 *
 * sourceId = selectedPdf.id for single-PDF plans, hubData.id for multi-PDF plans.
 *
 * Stored in Firestore collection: 'studyPlans'
 *
 * NOTE: Queries intentionally avoid compound where+orderBy to prevent requiring
 * Firestore composite indexes. Sorting is done client-side.
 */

import { db } from '../firebase/config';
import {
  collection, addDoc, getDocs, query,
  where, limit, doc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { saveResource } from './resourceCacheService';

const COLLECTION = 'studyPlans';

// ─── Save (upsert) ────────────────────────────────────────────────────────────

/**
 * Save (or update) the full study plan state for a source document.
 * Also mirrors a summary to Google Drive (non-blocking) when a plan exists.
 *
 * @param {string} userId
 * @param {string} sourceId   — pdfId (single) or hubId (multi)
 * @param {string} sourceName — human-readable name for display
 * @param {object} state      — { planMode, plan, answers, completedStepIds,
 *                                stepTimes, stepContents, quizResults, analytics, phase }
 * @returns {Promise<string|null>} Firestore document ID
 */
export const saveStudyPlanState = async (userId, sourceId, sourceName, state) => {
  if (!userId || !sourceId) return null;
  try {
    const data = {
      userId,
      sourceId,
      sourceName: sourceName || '',
      planMode:         state.planMode         ?? null,
      plan:             state.plan             ?? null,
      answers:          state.answers          ?? null,
      completedStepIds: state.completedStepIds ?? [],
      stepTimes:        state.stepTimes        ?? {},
      stepContents:     state.stepContents     ?? {},
      quizResults:      state.quizResults      ?? null,
      analytics:        state.analytics        ?? null,
      phase:            state.phase            ?? 'questionnaire',
      updatedAt:        serverTimestamp(),
    };
    // savedName is only written when the user explicitly names the plan.
    // Auto-saves omit this key so Firestore updateDoc preserves any existing savedName.
    if (state.savedName !== undefined) {
      data.savedName = state.savedName;
    }

    // Check for an existing document for this user + sourceId
    // NOTE: No orderBy here — avoids composite index requirement
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('sourceId', '==', sourceId),
      limit(1),
    );
    const snap = await getDocs(q);

    let docId;
    if (!snap.empty) {
      await updateDoc(doc(db, COLLECTION, snap.docs[0].id), data);
      docId = snap.docs[0].id;
    } else {
      const ref = await addDoc(collection(db, COLLECTION), {
        ...data,
        createdAt: serverTimestamp(),
      });
      docId = ref.id;
    }

    // ── Mirror plan summary to Google Drive (non-blocking) ──
    // Only sync when a plan has been generated (not during questionnaire)
    if (state.plan && userId && sourceId) {
      const completedCount = (state.completedStepIds ?? []).length;
      const totalDays = (state.plan?.days ?? []).length;
      const driveSummary = {
        title:            state.plan?.title    || sourceName,
        subject:          state.plan?.subject  || '',
        phase:            state.phase          || 'plan-view',
        planMode:         state.planMode       || '',
        completedSteps:   completedCount,
        totalDays,
        analytics:        state.analytics      || null,
        quizResults:      state.quizResults    || null,
        updatedAt:        new Date().toISOString(),
      };
      saveResource(
        userId, sourceId, sourceName || 'Study Plan', 'study_plan',
        `study_plan_${sourceId}`, driveSummary,
        { planMode: state.planMode, phase: state.phase },
      ).catch(() => {}); // fire and forget — never block the UI
    }

    return docId;
  } catch (err) {
    console.error('[studyPlanCacheService] saveStudyPlanState error:', err);
    return null;
  }
};

// ─── Load ──────────────────────────────────────────────────────────────────────

/**
 * Load the most recent saved plan state for a (userId, sourceId) pair.
 * Returns null if nothing found.
 *
 * NOTE: No orderBy in query to avoid composite index requirement.
 * Client-side sort is used when multiple docs exist for the same sourceId.
 */
export const loadStudyPlanState = async (userId, sourceId) => {
  if (!userId || !sourceId) return null;
  try {
    // Multiple equality filters don't require a composite index in Firestore
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('sourceId', '==', sourceId),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    // Sort client-side (newest first) in case there are multiple docs
    const docs = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const at = a.updatedAt?.toMillis?.() ?? 0;
        const bt = b.updatedAt?.toMillis?.() ?? 0;
        return bt - at;
      });
    return docs[0];
  } catch (err) {
    console.error('[studyPlanCacheService] loadStudyPlanState error:', err);
    return null;
  }
};

// ─── List all plans for a user ────────────────────────────────────────────────

/**
 * Load ALL saved study plans for a user, newest first.
 * Used by Learning Journey → Study Plans tab and Student Report.
 *
 * NOTE: No orderBy in Firestore query — avoids composite index on (userId, updatedAt).
 * Sorting is done client-side.
 *
 * @returns {Promise<Array>}
 */
export const getAllStudyPlans = async (userId) => {
  if (!userId) return [];
  try {
    // Single equality filter — no composite index needed
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
    );
    const snap = await getDocs(q);
    const plans = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Sort client-side: newest first
    return plans.sort((a, b) => {
      const at = a.updatedAt?.toMillis?.() ?? 0;
      const bt = b.updatedAt?.toMillis?.() ?? 0;
      return bt - at;
    });
  } catch (err) {
    console.error('[studyPlanCacheService] getAllStudyPlans error:', err);
    return [];
  }
};

// ─── Get all plans for a source ───────────────────────────────────────────────

/**
 * Load ALL saved plan documents for a (userId, sourceId) pair, newest first.
 * Includes both auto-saves and named snapshots. Only returns docs with a plan.
 */
export const getAllPlansForSource = async (userId, sourceId) => {
  if (!userId || !sourceId) return [];
  try {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('sourceId', '==', sourceId),
    );
    const snap = await getDocs(q);
    const docs = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(d => d.plan) // only fully-generated plans
      .sort((a, b) => {
        const at = (a.savedAt || a.updatedAt)?.toMillis?.() ?? 0;
        const bt = (b.savedAt || b.updatedAt)?.toMillis?.() ?? 0;
        return bt - at;
      });
    return docs;
  } catch (err) {
    console.error('[studyPlanCacheService] getAllPlansForSource error:', err);
    return [];
  }
};

// ─── Save a named plan snapshot ────────────────────────────────────────────────

/**
 * Save a named snapshot of the current study plan state.
 * Always creates a new document (never upserts) so multiple snapshots can coexist.
 *
 * @param {string} userId
 * @param {string} sourceId
 * @param {string} sourceName
 * @param {string} planName   — user-given name for this snapshot
 * @param {object} state      — same shape as saveStudyPlanState
 * @returns {Promise<string|null>} Firestore document ID
 */
export const saveNamedStudyPlan = async (userId, sourceId, sourceName, planName, state) => {
  if (!userId || !sourceId || !planName) return null;
  try {
    const ref = await addDoc(collection(db, COLLECTION), {
      userId,
      sourceId,
      sourceName:       sourceName        || '',
      savedName:        planName,
      isNamedSave:      true,
      planMode:         state.planMode         ?? null,
      plan:             state.plan             ?? null,
      answers:          state.answers          ?? null,
      completedStepIds: state.completedStepIds ?? [],
      stepTimes:        state.stepTimes        ?? {},
      stepContents:     state.stepContents     ?? {},
      quizResults:      state.quizResults      ?? null,
      analytics:        state.analytics        ?? null,
      phase:            state.phase            ?? 'plan-view',
      savedAt:          serverTimestamp(),
      updatedAt:        serverTimestamp(),
      createdAt:        serverTimestamp(),
    });
    return ref.id;
  } catch (err) {
    console.error('[studyPlanCacheService] saveNamedStudyPlan error:', err);
    return null;
  }
};

// ─── Clear ─────────────────────────────────────────────────────────────────────

/**
 * Delete all saved plan documents for a (userId, sourceId) pair.
 */
export const clearStudyPlanState = async (userId, sourceId) => {
  if (!userId || !sourceId) return;
  try {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('sourceId', '==', sourceId),
    );
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map(d => deleteDoc(doc(db, COLLECTION, d.id))));
  } catch (err) {
    console.error('[studyPlanCacheService] clearStudyPlanState error:', err);
  }
};
